"use client";
import { cn } from "visuais";

import { motion } from "motion/react";
import { useState, useEffect } from "react";

interface SpinnerProps {
  size?: "sm" | "md" | "lg";
  variant?: "gradient" | "solid";
  className?: string;
  withSound?: boolean;
}

export function Spinner({
  size = "md",
  variant = "gradient",
  className = "",
  withSound = false,
}: SpinnerProps) {
  // Estado para armazenar o tamanho calculado
  const [baseSize, setBaseSize] = useState(0);

  // Efeito para calcular o tamanho apenas no cliente
  useEffect(() => {
    const calculateSize = () => {
      const minDimension =
        typeof window !== "undefined"
          ? Math.min(window.innerWidth, window.innerHeight)
          : 300; // Valor padrão para SSR
      setBaseSize(minDimension * 0.3);
    };

    calculateSize(); // Calcula no mount
    window.addEventListener("resize", calculateSize); // Atualiza no redimensionamento

    return () => window.removeEventListener("resize", calculateSize); // Limpa o listener
  }, []);

  const sizeStyles = {
    sm: `h-[${Math.floor(baseSize * 0.5)}px] w-[${Math.floor(
      baseSize * 0.5
    )}px] border-2`,
    md: `h-[${Math.floor(baseSize * 0.7)}px] w-[${Math.floor(
      baseSize * 0.7
    )}px] border-4`,
    lg: `h-[${Math.floor(baseSize)}px] w-[${Math.floor(baseSize)}px] border-4`,
  };

  // Ajuste responsivo com Tailwind breakpoints
  const responsiveStyles = {
    sm: "max-h-16 max-w-16 sm:max-h-20 sm:max-w-20 lg:max-h-24 lg:max-w-24",
    md: "max-h-20 max-w-20 sm:max-h-24 sm:max-w-24 lg:max-h-32 lg:max-w-32",
    lg: "max-h-24 max-w-24 sm:max-h-32 sm:max-w-32 lg:max-h-40 lg:max-w-40",
  };

  // Som para acessibilidade auditiva (opcional)
  const playSound = () => {
    if (!withSound) return;
    const audio = new Audio("/sounds/loading-tone.mp3"); // Arquivo de som deve ser adicionado
    audio.loop = true;
    audio.volume = 0.3;
    audio.play().catch(() => console.log("Audio playback failed"));
    return () => audio.pause();
  };

  return (
    <div
      className={cn(
        "fixed inset-0 flex items-center justify-center z-50",
        "bg-background/80 backdrop-blur-sm",
        "shadow-subtle-dark"
      )}
      role="status"
      aria-label="Carregando..."
      onMouseEnter={playSound}
    >
      <motion.div
        className={cn(
          "relative rounded-full border-t-transparent",
          sizeStyles[size],
          responsiveStyles[size],
          variant === "solid" ? "border-primary" : "border-muted",
          className
        )}
        animate={{ rotate: 360 }}
        transition={{
          repeat: Infinity,
          duration: 1,
          ease: "linear",
        }}
      >
        <div
          className="absolute inset-0 rounded-full border-inherit"
          style={
            variant === "gradient"
              ? {
                  borderImage:
                    "linear-gradient(45deg, var(--chart-1), var(--chart-3), var(--chart-5)) 1",
                  borderStyle: "solid",
                  borderTopColor: "transparent",
                }
              : {
                  borderTopColor: "transparent",
                  borderStyle: "solid",
                }
          }
        />
        {variant === "gradient" && (
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "linear-gradient(to right, var(--chart-1)/20, var(--chart-3)/20)",
            }}
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ repeat: Infinity, duration: 1.2, ease: "easeInOut" }}
          />
        )}
      </motion.div>
      <span className="sr-only">Carregando...</span>
    </div>
  );
}
